import { css } from '@emotion/react'
import { Button, DataTable, HFlow, Icon, Text, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { Box } from 'components/Box'
import { useErrorHandler } from 'components/error'
import { PopperButton, PopperControls } from 'components/popper'
import {
  IsPainelChamadasHabilitadoDocument,
  useParearDispositivoPainelMutation,
  useRemoverDispositivoPainelMutation,
} from 'graphql/hooks.generated'
import { formatDate } from 'util/date/formatDate'
import {
  CadastrarDispositivoPainelChamadasModel,
  DispositivoPainelChamadaRow,
  PermissaoPainelChamadas,
} from 'view/chamada-painel/model-chamadaPainel'

import { CadastrarDispositivoPainelChamadasForm } from './CadastrarDispositivoPainelChamadasForm'

interface CadastrarDispositivoPainelChamadasBoxProps {
  dispositivosPareados: ReadonlyArray<DispositivoPainelChamadaRow>
  loadingDispositivosPareados: boolean
  unidadeSaudeId: ID
  permissao: PermissaoPainelChamadas
  onCreate(): void
  onDelete(): void
}

export const CadastrarDispositivoPainelChamadasBox = (props: Readonly<CadastrarDispositivoPainelChamadasBoxProps>) => {
  const { dispositivosPareados, loadingDispositivosPareados, unidadeSaudeId, onCreate, onDelete, permissao } = props

  const [parearDispositivoPainel] = useParearDispositivoPainelMutation()
  const [removerDispositivoPainel, { loading: isLoadingRemover }] = useRemoverDispositivoPainelMutation()
  const handleRejection = useErrorHandler()
  const alert = useAlert()

  const handleDelete = (id: ID) => {
    removerDispositivoPainel({
      variables: { id },
      refetchQueries: [{ query: IsPainelChamadasHabilitadoDocument, variables: { unidadeSaudeId } }],
    })
      .then(() => {
        alert('success', 'Dispositivo excluído com sucesso.')
        onDelete()
      })
      .catch(handleRejection)
  }

  const renderPopper = (popperProps: PopperControls) => {
    const handleSubmit = (values: CadastrarDispositivoPainelChamadasModel) => {
      return parearDispositivoPainel({
        variables: {
          input: {
            nome: values.nome,
            pinUsuario: values.codigo,
            unidadeSaudeId: unidadeSaudeId,
          },
        },
      })
        .then(() => {
          popperProps.close()
          alert('success', 'Dispositivo cadastrado com sucesso.')
          onCreate()
        })
        .catch(handleRejection)
    }

    return <CadastrarDispositivoPainelChamadasForm onSubmit={handleSubmit} permissao={permissao} />
  }

  return (
    <Box>
      <VFlow>
        <Text>
          Para adicionar um dispositivo, utilize o navegador do dispositivo para acessar o link do painel e utilize o
          código apresentado na tela para cadastrá-lo em "Adicionar dispositivo". É recomendado utilizar um dispositivo
          independente e que possa emitir som. Para uma melhor experiência, o painel deve ser utilizado em tela cheia.
        </Text>
        <HFlow justifyContent='flex-end'>
          <Tooltip text={permissao.hint ?? undefined}>
            <PopperButton
              placement='bottom-end'
              kind='primary'
              skin='outline'
              size='small'
              renderPopper={renderPopper}
              disabled={!permissao.enabled}
            >
              <HFlow hSpacing={0.5}>
                <Icon icon='plus' />
                Adicionar dispositivo
              </HFlow>
            </PopperButton>
          </Tooltip>
        </HFlow>
        <DataTable<DispositivoPainelChamadaRow>
          rows={dispositivosPareados}
          loading={loadingDispositivosPareados}
          columns={[
            {
              name: 'data',
              style: styles.dataColumn,
              header: 'Data de pareamento',
              render: (row) => formatDate(row.dataPareamento),
            },
            { name: 'nome', header: 'Nome do dispositivo', render: (row) => row.nome },
            {
              name: 'actions',
              header: '',
              align: 'right',
              render: (row) => (
                <Tooltip text={permissao.enabled ? 'Excluir' : permissao.hint ?? undefined}>
                  <Button
                    skin='ghost'
                    size='small'
                    disabled={isLoadingRemover || !permissao.enabled}
                    loading={isLoadingRemover}
                    onClick={() => handleDelete(row.id)}
                  >
                    <Icon icon='trashOutline' />
                  </Button>
                </Tooltip>
              ),
            },
          ]}
        />
      </VFlow>
    </Box>
  )
}

const styles = {
  dataColumn: css`
    width: 10rem;
  `,
}
